import type React from "react";
import {
  type ElementType,
  forwardRef,
  type ImgHTMLAttributes,
  type SyntheticEvent,
  useEffect,
  useState,
} from "react";

import * as styles from "./styles.css";

export interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  as?: ElementType;
}

export const Image = forwardRef(function Image(
  { as: Component = "img", ...props }: ImageProps,
  ref: React.Ref<HTMLImageElement>,
) {
  const {
    src,
    width,
    height,
    style: consumerStyle,
    className: consumerClassName = "",
    onError,
    ...rest
  } = props;
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setErrored(false);
  }, [src]);

  const handleError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    setErrored(true);
    onError?.(e);
  };

  if (src && !errored) {
    return <Component ref={ref} {...props} src={src} onError={handleError} />;
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      className={`${styles.placeholderContainer} ${consumerClassName}`}
      style={{
        width: width != null ? width : undefined,
        height: height != null ? height : undefined,
        ...consumerStyle,
      }}
      {...rest}
    />
  );
});

Image.displayName = "Image";
